import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { ensureDownloadToken, clearDownloadToken } from "@/lib/downloadToken";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(undefined); // undefined = loading, null = anon
    const [ready, setReady] = useState(false);

    // Session lives in a Secure httpOnly cookie — the browser sends it
    // automatically, so /auth/me is the single source of truth.
    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            ensureDownloadToken(true);
            return data;
        } catch (_e) {
            setUser(null);
            clearDownloadToken();
            return null;
        } finally {
            setReady(true);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login-mfa", { email, password });
        // MFA-required response — hand back to caller to render the challenge.
        if (data?.mfa_required) {
            return {
                mfa_required: true,
                challenge_token: data.challenge_token,
                method: data.method,
                email_hint: data.email_hint,
                expires_in: data.expires_in,
            };
        }
        setUser(data.user);
        ensureDownloadToken(true);
        return data.user;
    }, []);

    const completeMfaChallenge = useCallback(async (challengeToken, code) => {
        const { data } = await api.post("/auth/mfa/challenge", {
            challenge_token: challengeToken,
            code,
        });
        setUser(data.user);
        ensureDownloadToken(true);
        return data.user;
    }, []);

    const register = useCallback(async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        if (data.user) setUser(data.user);
        return data;
    }, []);

    const logout = useCallback(() => {
        // Server clears the httpOnly cookie; local state drops immediately.
        api.post("/auth/logout").catch(() => {});
        setUser(null);
        clearDownloadToken();
    }, []);

    // Stable value reference — prevents every consumer from re-rendering on
    // every AuthProvider render, and keeps `refresh` referentially stable for
    // dependent effects (e.g. useEffect(..., [refresh])).
    const value = useMemo(
        () => ({ user, ready, login, completeMfaChallenge, register, logout, refresh }),
        [user, ready, login, completeMfaChallenge, register, logout, refresh],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
